import { NgTemplateOutlet } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    ElementRef,
    Injector,
    Component,
    DestroyRef,
    effect,
    inject,
    viewChild,
    type TemplateRef,
    computed,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import { take } from 'rxjs';
import { QpxButton, QpxIcon, QpxTooltip } from '../../../directives';
import { injectPopover, type PopoverRef } from '../../../services';
import {
    PDF_PREVIEW_MAX_ZOOM,
    PDF_PREVIEW_MIN_ZOOM,
    type PreviewZoom,
} from '../models';
import { PreviewZoomPopover } from './preview-zoom-popover';

interface ZoomOption {
    readonly label: string;
    readonly value: PreviewZoom;
    readonly keywords?: readonly string[];
}

const ZOOM_OPTIONS: readonly ZoomOption[] = [
    { label: 'Reset', value: 'auto', keywords: ['auto', 'fit'] },
    { label: 'Full width', value: 'page-width', keywords: ['width'] },
    { label: 'Fit', value: 'page-fit', keywords: ['fit page'] },
    { label: 'Actual size', value: 'page-actual', keywords: ['100'] },
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 },
    { label: '125%', value: 125 },
    { label: '150%', value: 150 },
    { label: '200%', value: 200 },
    { label: '300%', value: 300 },
    { label: '400%', value: 400 },
    { label: '600%', value: 600 },
    { label: '800%', value: 800 },
    { label: '1000%', value: 1000 },
    { label: '1500%', value: 1500 },
    { label: '2000%', value: 2000 },
];

@Component({
    selector: 'qpx-preview-zoom-control',
    imports: [
        NgTemplateOutlet,
        MatButtonModule,
        QpxTooltip,
        NgIcon,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './preview-zoom-control.html',
    styleUrl: './preview-zoom-control.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'contents',
    },
})
export class PreviewZoomControl {
    private readonly popover = injectPopover();
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly zoomOutButton = viewChild<
        unknown,
        ElementRef<HTMLButtonElement>
    >('zoomOutButton', { read: ElementRef });
    protected readonly mobile = injectViewportBreakpoints().isPhone;
    private readonly contents =
        viewChild.required<TemplateRef<unknown>>('zoomContents');
    protected readonly zoomPopover = signal<PopoverRef | null>(null);

    constructor() {
        effect(() => {
            if (!this.mobile()) this.zoomPopover()?.close();
        });
        this.destroyRef.onDestroy(() => this.zoomPopover()?.close());
    }

    protected openZoomPopover(event: MouseEvent): void {
        const existing = this.zoomPopover();
        if (existing && !existing.closed()) {
            existing.close();
            return;
        }
        const origin = event.currentTarget as HTMLButtonElement;
        const ref = this.popover.open({
            origin,
            loadComponent: async () => PreviewZoomPopover,
            componentInputs: {
                content: this.contents(),
                label: `${this.ariaLabel()} controls`,
            },
            closeOnOutsidePointer: true,
            positions: [
                {
                    originX: 'center',
                    originY: 'top',
                    overlayX: 'center',
                    overlayY: 'bottom',
                    offsetY: -8,
                },
            ],
        });
        this.zoomPopover.set(ref);
        ref.afterClosed()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.zoomPopover.set(null);
                if (!this.mobile() && !this.destroyRef.destroyed) {
                    afterNextRender(
                        () => this.zoomOutButton()?.nativeElement.focus(),
                        {
                            injector: this.injector,
                        }
                    );
                } else if (origin.isConnected) {
                    origin.focus();
                }
            });
    }

    //#region Inputs
    public readonly zoom = input.required<PreviewZoom>();
    public readonly ariaLabel = input.required<string>();
    public readonly optionsId = input.required<string>();
    public readonly disabled = input(false);
    public readonly minimumZoom = input(PDF_PREVIEW_MIN_ZOOM);
    public readonly maximumZoom = input(PDF_PREVIEW_MAX_ZOOM);
    //#endregion

    //#region Outputs
    public readonly zoomOut = output<void>();
    public readonly zoomIn = output<void>();
    public readonly zoomReset = output<void>();
    public readonly zoomSelect = output<PreviewZoom>();
    //#endregion

    //#region State
    protected readonly zoomMenuOpen = signal(false);
    protected readonly zoomQuery = signal<string | null>(null);
    protected readonly activeOptionIndex = signal(-1);
    protected readonly zoomDisplay = computed(() =>
        this.formatZoom(this.zoom())
    );
    protected readonly zoomInputValue = computed(
        () => this.zoomQuery() ?? this.zoomDisplay()
    );
    private readonly zoomOptions = computed(() =>
        ZOOM_OPTIONS.filter(
            (option) =>
                typeof option.value !== 'number' ||
                (option.value >= this.minimumZoom() &&
                    option.value <= this.maximumZoom())
        )
    );
    protected readonly filteredZoomOptions = computed(() => {
        const zoomQuery = this.zoomQuery() ?? '';
        const query = this.normalizeSearch(zoomQuery);
        const options = query
            ? this.zoomOptions().filter((option) =>
                  this.matchesZoomOption(option, query)
              )
            : [...this.zoomOptions()];
        const customZoom = this.parseZoomPercent(zoomQuery);

        if (
            customZoom != null &&
            !options.some((option) => option.value === customZoom)
        ) {
            return [{ label: `${customZoom}%`, value: customZoom }, ...options];
        }

        return options;
    });
    protected readonly activeOptionId = computed(() => {
        const index = this.activeOptionIndex();
        return this.zoomMenuOpen() && index >= 0 ? this.optionId(index) : null;
    });
    //#endregion

    //#region Template methods
    protected onZoomInput(event: Event): void {
        this.zoomQuery.set((event.target as HTMLInputElement).value);
        this.zoomMenuOpen.set(true);
        this.activeOptionIndex.set(this.filteredZoomOptions().length ? 0 : -1);
    }

    protected onZoomFocus(): void {
        this.zoomQuery.set('');
        this.zoomMenuOpen.set(true);
        this.activeOptionIndex.set(this.selectedOptionIndex());
    }

    protected onZoomBlur(): void {
        setTimeout(() => {
            this.zoomMenuOpen.set(false);
            this.zoomQuery.set(null);
            this.activeOptionIndex.set(-1);
        });
    }

    protected onZoomKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveActiveOption(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveActiveOption(-1);
                break;
            case 'Enter':
                event.preventDefault();
                this.applyActiveOptionOrQuery();
                break;
            case 'Escape':
                event.preventDefault();
                this.zoomMenuOpen.set(false);
                this.zoomQuery.set(null);
                this.activeOptionIndex.set(-1);
                break;
        }
    }

    protected selectZoomOption(option: ZoomOption): void {
        this.applyZoom(option.value);
    }

    protected optionId(index: number): string {
        return `${this.optionsId()}-option-${index}`;
    }

    protected activateOption(index: number): void {
        this.activeOptionIndex.set(index);
    }
    //#endregion

    //#region Private methods
    private applyZoomQuery(): void {
        const zoomQuery = this.zoomQuery() ?? '';
        const query = this.normalizeSearch(zoomQuery);
        const option = this.zoomOptions().find((candidate) =>
            this.matchesZoomOption(candidate, query)
        );
        const customZoom = this.parseZoomPercent(zoomQuery);

        this.applyZoom(option?.value ?? customZoom ?? this.zoom());
    }

    private applyActiveOptionOrQuery(): void {
        const option = this.filteredZoomOptions()[this.activeOptionIndex()];
        if (option) {
            this.applyZoom(option.value);
            return;
        }

        this.applyZoomQuery();
    }

    private applyZoom(zoom: PreviewZoom): void {
        if (zoom === 'auto') {
            this.zoomReset.emit();
        } else {
            this.zoomSelect.emit(zoom);
        }

        this.zoomMenuOpen.set(false);
        this.zoomQuery.set(null);
        this.activeOptionIndex.set(-1);
    }

    private moveActiveOption(direction: -1 | 1): void {
        this.zoomMenuOpen.set(true);
        const options = this.filteredZoomOptions();
        if (!options.length) {
            this.activeOptionIndex.set(-1);
            return;
        }

        const current = this.activeOptionIndex();
        const next =
            current < 0
                ? direction > 0
                    ? 0
                    : options.length - 1
                : (current + direction + options.length) % options.length;
        this.activeOptionIndex.set(next);
    }

    private selectedOptionIndex(): number {
        const index = this.filteredZoomOptions().findIndex(
            (option) => this.zoomDisplay() === option.label
        );
        return index >= 0 ? index : 0;
    }

    private formatZoom(zoom: string | number): string {
        if (typeof zoom === 'number') return `${Math.round(zoom)}%`;

        const numericZoom = Number.parseInt(zoom, 10);
        if (!Number.isNaN(numericZoom)) return `${numericZoom}%`;

        switch (zoom) {
            case 'page-width':
                return 'Full width';
            case 'page-fit':
                return 'Fit';
            case 'page-actual':
                return '100%';
            default:
                return 'Auto';
        }
    }

    private matchesZoomOption(option: ZoomOption, query: string): boolean {
        const values = [
            option.label,
            String(option.value),
            ...(option.keywords ?? []),
        ];

        return values.some((value) =>
            this.normalizeSearch(value).includes(query)
        );
    }

    private normalizeSearch(value: string): string {
        return value.toLowerCase().replaceAll(/\s|%|-/g, '');
    }

    private parseZoomPercent(value: string): number | null {
        const numericZoom = Number.parseInt(value.replace('%', ''), 10);
        if (Number.isNaN(numericZoom)) return null;

        return Math.min(
            Math.max(numericZoom, this.minimumZoom()),
            this.maximumZoom()
        );
    }
    //#endregion
}
